import i18n from '../i18n/config';

/**
 * Locale-aware number formatting utilities
 * Requirements: 5.4, 5.5
 */

/**
 * Get the current locale for Intl APIs
 * Maps i18n language codes to locale strings
 */
function getCurrentLocale(): string {
  const currentLang = i18n.language;
  
  switch (currentLang) {
    case 'ru':
      return 'ru-RU';
    case 'uz':
      return 'uz-UZ';
    case 'en':
    default:
      return 'en-US';
  }
}

/**
 * Format a number according to the current locale
 * Requirement 5.4: Format numbers with appropriate thousand separators and decimal points
 * 
 * @param value - Number to format
 * @param options - Intl.NumberFormat options
 * @returns Formatted number string in current locale
 */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions
): string {
  const locale = getCurrentLocale();
  return new Intl.NumberFormat(locale, options).format(value);
}

/**
 * Format a file size with localized units
 * Requirement 5.5: Use localized unit labels (KB, MB, GB)
 * 
 * @param bytes - File size in bytes
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted file size string with localized units
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) {
    return i18n.t('fileSize.bytes', { size: '0' });
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;

  if (bytes < k) {
    return i18n.t('fileSize.bytes', { size: bytes.toString() });
  }

  if (bytes < k * k) {
    const size = (bytes / k).toFixed(dm);
    return i18n.t('fileSize.kilobytes', { size });
  }

  if (bytes < k * k * k) {
    const size = (bytes / (k * k)).toFixed(dm);
    return i18n.t('fileSize.megabytes', { size });
  }

  const size = (bytes / (k * k * k)).toFixed(dm);
  return i18n.t('fileSize.gigabytes', { size });
}

/**
 * Format a percentage according to the current locale
 * 
 * @param value - Percentage value (0-100)
 * @param decimals - Number of decimal places (default: 0)
 * @returns Formatted percentage string
 */
export function formatPercentage(value: number, decimals: number = 0): string {
  return formatNumber(value, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/**
 * Format a currency value according to the current locale
 * 
 * @param value - Currency value
 * @param currency - Currency code (e.g., 'USD', 'EUR', 'RUB')
 * @returns Formatted currency string
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  return formatNumber(value, {
    style: 'currency',
    currency
  });
}
